import { polyLine2ArrayEdgeMapSlow } from "geom/closestPoint";
import { polyLine2EdgeCount, polyLine2IsClosed } from "geom/polyLine2";
import type { PolyLine2, PolyLine2Polygon } from "geom/polyLine2Types";

/**
 * NOTICE - THIS FILE DOES NOT KNOW ABOUT HALF EDGES. IT IS A GENERIC DATA TOOL THAT CAN BE USED FOR ANY DATA MAPPING WITH
 * POLYGONS, POLYLINE2S, AND EDGES.
 */

/**
 * Convert Data mapped to vertices of a PolyLine2 to data mapped to edges in an array.
 *
 * VertexData uses the format such that a PolyLine2 `[x,y,x2,y2,x3,y3,x,y]` would have data `[a,a,b,b,c,c,d,d,a,a]`.
 * Each x and y component has a duplicate value.
 *
 * EdgeData would map the same data to edges `[a,b,c,d]`. Additionally, because more data is coming in, the data is stored in an array.
 * This results in `[[a],[b],[c],[d]]`.
 *
 */
export function vertexDataToEdgeDataAsArray<T>(vertexData: T[], edgeData: T[][]) {
  if (!vertexData) {
    return;
  }

  for (let i = 0; i < vertexData.length - 2; i += 2) {
    edgeData[i / 2] = [vertexData[i]];
  }
}

/**
 * See {@link vertexDataToEdgeDataAsArray} for more information. This function is a wrapper for nested arrays.
 */
export function vertexDataArrToEdgeDataAsArrayArr<T>(vertexDataArr: T[][]): T[][][] {
  const edgeDataArr: T[][][] = [];
  for (const vertexData of vertexDataArr) {
    const edgeData: T[][] = [];
    vertexDataToEdgeDataAsArray<T>(vertexData, edgeData);
    edgeDataArr.push(edgeData);
  }

  return edgeDataArr;
}

/**
 * Remap edge data from a PolyLine2 to another where the target polyline is overlapping the source polyline.
 *
 * @param combineData Is the function that determines how data is combined when edges are mapped to the same target index.
 */
export function edgeDataRemap<T>(
  remap: { [index: number]: number[] },
  edgeData: T[][],
  combineData: (a: T[], b: T[]) => T[],
  targetEdgeCount: number,
  defaultT: T,
) {
  const newEdgeData: T[][] = [];
  for (const edgeIndex in remap) {
    const results = remap[edgeIndex];
    const data = edgeData[edgeIndex];

    if (results === undefined) {
      continue;
    }

    for (const newEdgeId of results) {
      const res = newEdgeData[newEdgeId];

      if (res) {
        newEdgeData[newEdgeId] = combineData(res, data);
        continue;
      }

      newEdgeData[newEdgeId] = Array.isArray(data) ? [...data] : data;
    }
  }

  for (let i = 0; i < targetEdgeCount; i++) {
    if (newEdgeData[i] === undefined) {
      newEdgeData[i] = [defaultT];
    }
  }

  return newEdgeData;
}

/**
 * See {@link edgeDataRemap} for more information. This function is a wrapper for nested arrays.
 */
export function edgeDataArrRemap<T>(
  pls: PolyLine2[],
  plsTarget: PolyLine2[],
  edgeDataArr: T[][][],
  combineData: (a: T[], b: T[]) => T[],
  defaultT: T,
) {
  return edgeDataArrInnerRemap(
    polyLine2ArrayEdgeMapSlow(pls, plsTarget),
    edgeDataArr,
    combineData,
    plsTarget?.map((x) => polyLine2EdgeCount(x)),
    defaultT,
  );
}

function edgeDataArrInnerRemap<T>(
  remap: { [index: number]: number[] }[],
  edgeDataArr: T[][][],
  combineData: (a: T[], b: T[]) => T[],
  edgeCounts: number[],
  defaultT: T,
) {
  return remap.map((r, index) => {
    return edgeDataRemap(r, edgeDataArr[index], combineData, edgeCounts[index], defaultT);
  });
}

export function polygonDataReplaceIfLengthMismatch<T>(polygon: PolyLine2Polygon, polygonData: T[][][], defaultT: T) {
  const polygonDataOut: T[][][] = [];

  for (let i = 0; i < polygon.length; i++) {
    const polyLine2 = polygon[i];
    const polyLine2Data = polygonData[i];

    polygonDataOut.push(polyLine2DataReplaceIfLengthMismatch(polyLine2, polyLine2Data, defaultT));
  }

  return polygonDataOut;
}

/**
 * Rather than deploying mismatched data and having users embed incorrect half edge data into selectors in the doc,
 * this method will put default data in place and effectively disallow selection.
 */
export function polyLine2DataReplaceIfLengthMismatch<T>(pl: PolyLine2, plData: T[][] | undefined, defaultT: T): T[][] {
  if (polyLine2IsClosed(pl)) {
    const targetDataLength = pl.length / 2 - 1;
    if (!plData || plData.length !== targetDataLength) {
      return new Array(targetDataLength).fill(defaultT);
    } else {
      return plData;
    }
  } else {
    const targetDataLength = pl.length / 2;
    if (!plData || plData.length !== targetDataLength) {
      return new Array(targetDataLength).fill(defaultT);
    } else {
      return plData;
    }
  }
}

export function polyLine2DefaultDataFill<T>(pl: PolyLine2, defaultT: T): T[][] {
  if (polyLine2IsClosed(pl)) {
    return new Array(pl.length / 2).fill([defaultT]);
  } else {
    return new Array(pl.length / 2).fill([defaultT]);
  }
}

/**
 * For instances, where the outer ring has data but the inner rings need backfilled undefined data
 */
export function polygonInnerRingDefaultDataFill<T>(
  t: T[][],
  s: PolyLine2[],
  polyLine2TCopyFunction: (data: T[][]) => T[][],
  defaultT: T,
) {
  const newE: T[][][] = [];
  newE.push(polyLine2TCopyFunction(t));
  for (const polyLine of s) {
    newE.push(polyLine2DefaultDataFill(polyLine, defaultT));
  }
  return newE;
}

/**
 * Enables properly collating data for a polygon where some rings might have already be closed and others not.
 *
 * @param isClosingSegmentNeededPerRing flag to indicate whether a polyLine2 was closed
 * @param polygonData edge data to be amended to include a closing segment
 */
export function polygonAddClosingSegmentDefaultData<T>(
  isClosingSegmentNeededPerRing: boolean[],
  polygonData: T[][][],
  defaultT: T,
) {
  for (let i = 0; i < isClosingSegmentNeededPerRing.length; i++) {
    const requiredClosing = isClosingSegmentNeededPerRing[i];
    if (requiredClosing) {
      polygonData[i].push([defaultT]);
    }
  }
}

/**
 * Enables properly reversing data for a polygon where some rings might have already be reversed and others not.
 *
 * @param isWindingFlippedPerRing flag to indicate whether a polyLine2's winding was flipped
 * @param polygonData edge data to be reversed if winding was flipped
 */
export function polygonAdjustWindingData<T>(isWindingFlippedPerRing: boolean[], polygonData: T[][]) {
  for (let i = 0; i < isWindingFlippedPerRing.length; i++) {
    const windingFlipped = isWindingFlippedPerRing[i];
    if (windingFlipped && polygonData[i] !== undefined) {
      polygonData[i] = polygonData[i].reverse();
    }
  }
}

export function polyLine2DataAdjustWinding<T>(isWindingFlipped: boolean, polyLine2Data: T[]) {
  if (isWindingFlipped) {
    return polyLine2Data.reverse();
  }
  return polyLine2Data;
}

export function mapDataToEdgeIndexMap(edgeIndexMap: (number | undefined)[], dataIn: number[]) {
  const dataOut: (number | undefined)[] = [];
  const edgeMapKeysLength = Object.keys(edgeIndexMap).length;
  for (let i = 0; i < edgeMapKeysLength; i++) {
    const newEdgeIndex = edgeIndexMap[i];
    dataOut[i] = newEdgeIndex === undefined ? undefined : dataIn[newEdgeIndex];
  }
  return dataOut;
}

export function arrayDataToObjectArrayData(remap: number[]) {
  const remapObj: { [index: number]: number[] } = {};
  for (const [key, value] of remap.entries()) {
    if (value !== undefined) {
      remapObj[key] = [value];
    }
  }
  return remapObj;
}
